import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import {
  ChefHat,
  Home as HomeIcon,
  BookOpen,
  UserCircle,
  History,
  LogOut,
  ArrowLeft
} from "lucide-react";
import HomePage from "./pages/HomePage";
import HistoryPage from "./pages/HistoryPage";
import LibraryPage from "./pages/LibraryPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import MealDetailPage from "./pages/MealDetailPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Top Bar - Slim: Logo left, History + Logout right. Back arrow on sub-pages.
const TopBar = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";

  const pageTitle = {
    "/history": "History",
    "/library": "All Meals",
    "/profile": "Profile",
    "/admin": "Admin",
  }[location.pathname];

  const handleLogout = () => {
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userId");
    localStorage.removeItem("memberId");
    localStorage.removeItem("todaysMeals");
    localStorage.removeItem("todaysMealsDate");
    localStorage.removeItem("mealPlans");
    localStorage.removeItem("hasSeenWelcome");
    localStorage.removeItem("learningBannerDismissed");
    localStorage.removeItem("userSignupDate");
    window.location.href = "/";
  };

  return (
    <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/40" data-testid="top-bar">
      <div className="app-container py-3 flex items-center justify-between">
        {isHome ? (
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <ChefHat size={18} className="text-primary" strokeWidth={1.5} />
            </div>
            <span className="font-semibold text-foreground text-sm">Aaj Khane</span>
          </Link>
        ) : (
          <Link to="/" className="flex items-center gap-2 group" data-testid="back-to-home-btn">
            <button className="w-9 h-9 rounded-full bg-muted/50 flex items-center justify-center group-hover:bg-muted transition-colors">
              <ArrowLeft size={18} className="text-foreground" strokeWidth={1.5} />
            </button>
            <span className="font-semibold text-foreground text-sm">{pageTitle || "Back"}</span>
          </Link>
        )}

        <div className="flex items-center gap-1">
          {isHome && (
            <Link to="/history" data-testid="nav-history">
              <button
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/50"
              >
                <History size={16} strokeWidth={1.5} />
                <span>History</span>
              </button>
            </Link>
          )}
          <button
            onClick={handleLogout}
            className="rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </nav>
  );
};

// Bottom Bar - Fixed: Plan, All Meals, Profile
const BottomBar = () => {
  const location = useLocation();

  const tabs = [
    { path: "/", label: "Plan", icon: HomeIcon, testId: "nav-home" },
    { path: "/library", label: "All Meals", icon: BookOpen, testId: "nav-library" },
    { path: "/profile", label: "Profile", icon: UserCircle, testId: "nav-profile" },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-[10000] bg-background border-t border-border/40 safe-area-bottom" data-testid="bottom-bar">
      <div className="flex justify-around items-center py-2 px-4">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = location.pathname === tab.path;
          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={`flex flex-col items-center gap-0.5 px-4 py-1 rounded-xl transition-colors ${
                isActive
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={tab.testId}
            >
              <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
              <span className={`text-xs ${isActive ? "font-semibold" : "font-medium"}`}>
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

// Footer
const Footer = () => (
  <footer className="border-t border-border/40 mt-2">
    <div className="app-container py-3 text-center">
      <p className="text-sm text-muted-foreground">
        Food should nourish, not stress. Made with <span className="text-primary">care</span>.
      </p>
    </div>
  </footer>
);

// Main App Content (needs to be inside BrowserRouter for useLocation)
function AppContent({ isLoggedIn }) {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [showBottomBar, setShowBottomBar] = useState(false);

  return (
    <>
      {isLoggedIn && <TopBar />}
      <main className={`${showBottomBar ? "pb-20" : ""}`}>
        <Routes>
          <Route path="/" element={<HomePage onBottomBarChange={setShowBottomBar} />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/meal/:mealId" element={<MealDetailPage />} />
        </Routes>
      </main>
      {isLoggedIn && !showBottomBar && <Footer />}
      {showBottomBar && <BottomBar />}
    </>
  );
}

// App version — bump this to force all existing users to re-login on next visit
const APP_VERSION = "2.1";

// Main App
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Version check: if version changed, clear all cached data and force fresh start
    const storedVersion = localStorage.getItem("appVersion");
    if (storedVersion !== APP_VERSION) {
      localStorage.clear();
      localStorage.setItem("appVersion", APP_VERSION);
      return false;
    }
    return !!localStorage.getItem("userName");
  });

  // Seed database once on app startup
  useEffect(() => {
    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  // Listen for storage changes to update login state
  useEffect(() => {
    const checkLogin = () => {
      const loggedIn = !!localStorage.getItem("userName");
      setIsLoggedIn(prev => {
        if (prev !== loggedIn) return loggedIn;
        return prev;
      });
    };
    window.addEventListener("storage", checkLogin);
    window.addEventListener("loginStateChanged", checkLogin);
    return () => {
      window.removeEventListener("storage", checkLogin);
      window.removeEventListener("loginStateChanged", checkLogin);
    };
  }, []);

  return (
    <div className="flex flex-col">
      <BrowserRouter>
        <AppContent isLoggedIn={isLoggedIn} />
      </BrowserRouter>
      <Toaster position="bottom-center" richColors />
    </div>
  );
}

export default App;
